/**
 * JsonInput — Monaco-backed JSON editor for a workflow trigger payload,
 * pre-filled with an example derived from the workflow's JSON Schema.
 *
 * Why not a field-by-field form?
 *   Power users (the primary audience for "Trigger" on a deployed pipeline)
 *   are devs who already know JSON; pasting a known-good payload from
 *   their last run is faster than tabbing through inputs. The schema is
 *   already available — we use it to seed the example so they're not
 *   starting from a blank editor.
 *
 *   For non-devs, the form-renderer variant lives next door in SchemaForm.js
 *   and can be opted into later via a tab toggle if there's demand.
 *
 * Field docs live INSIDE the editor:
 *   Instead of a separate field-reference list below the editor (which
 *   duplicated the keys already on screen), a Monaco hover provider surfaces
 *   each documented field's type / required-ness / description as a tooltip
 *   when you point at the key. A one-line hint in the toolbar tells the user
 *   the fields are hoverable.
 *
 *   Required fields are additionally flagged with a ghost "// required" label
 *   injected (Monaco injected-text decoration) at the end of the field's line.
 *   It's an inlay hint — NOT part of the JSON, so it never reaches JSON.parse
 *   and draws no error squiggle.
 *
 * Validation:
 *   The only client-side check is "does it parse as JSON" — the server
 *   is authoritative for schema validation. We report `ok=false` if the
 *   editor contents don't parse; the parent disables submit.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { schemaToExample, prettyJson, isTriggerHidden, isAdvancedField } from './schema-example';
import './JsonInput.css';

// Build a name → { type, required, description } map of the schema's
// documented properties. Drives the hover provider (tooltip contents). Only
// props with a non-empty description are "documented" — those are the keys we
// can actually say something useful about on hover.
function buildPropertyDocs(schema, isReqField, hideInternal, hideAdvanced) {
  const docs = new Map();
  if (!schema?.properties) return docs;
  for (const [name, def] of Object.entries(schema.properties)) {
    if (hideInternal && isTriggerHidden(def)) continue;
    // Advanced fields that aren't in the seed (collapsed) have no key on screen
    // to hover, so omit their docs too — unless required (always seeded).
    if (hideAdvanced && !isReqField(name, def) && isAdvancedField(def)) continue;
    const description = def.description || '';
    if (!description) continue;
    docs.set(name, {
      type: def.type || (def.enum ? 'enum' : 'any'),
      required: isReqField(name, def),
      description,
    });
  }
  return docs;
}

export default function JsonInput({
  schema,         // JSON Schema (workflow.graph.stateSchema). Optional.
  onChange,       // (parsedValue, { ok, error }) => void
  disabled = false,
  initialValue,   // Optional plain JS object. When provided, takes
                  // precedence over schema-derived example (e.g. the
                  // user's saved `defaultInput` from the workflow row).
  hideInternal = false, // when true, omit trigger-hidden props (self-host-only
                        // / comment_reply-only) from the seeded example payload
                        // AND from the in-editor hover docs.
  hideAdvanced = false, // when true, omit ADVANCED-class props (collapsed behind
                        // the modal's "Advanced" expander) from the seeded
                        // payload + hover docs — required fields always stay.
  label = 'Trigger payload (JSON)', // toolbar heading. Distinguishes the
                        // one-off Trigger editor from the persisted Schedule
                        // input editor (same widget, different intent).
}) {
  // Seed precedence: user-saved defaultInput (initialValue) → schema-derived example.
  // Empty object initialValue is allowed (means "user wants empty default").
  //
  // The generated example is the agent's REAL input surface ('seed' mode): the
  // required fields PLUS every field that carries a default — so the configured
  // value is visible and editable (e.g. github-ai-scout shows since:"weekly",
  // maxCandidates:30, its rubric, …). It deliberately DROPS noise: AGENT-LEVEL
  // config set at deploy (`model`) and pure-optional fields with no default
  // (which would just seed as empty stubs and read as "you must fill this").
  // So a config-only agent never seeds an empty `{}`, while a fix-style agent
  // still seeds just its required input (Sentry fix → { instruction, trigger }).
  // Dropped fields stay fully VALID input (add them by hand) and are discoverable
  // via the in-editor hover docs.
  const seedFromProps = () => {
    if (initialValue !== undefined && initialValue !== null) {
      return prettyJson(initialValue);
    }
    return prettyJson(schemaToExample(schema, { mode: 'seed', hideInternal, hideAdvanced }));
  };
  const [text, setText] = useState(seedFromProps);
  const [parseError, setParseError] = useState(null);

  // Monaco handles. The editor + monaco namespace are captured onMount; the
  // hover-provider disposable and the current decoration ids are tracked so we
  // can clean up / re-apply across edits and unmounts.
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const hoverDisposableRef = useRef(null);
  const decorationsRef = useRef([]);

  const requiredSet = new Set(Array.isArray(schema?.required) ? schema.required : []);
  // A field is effectively required FOR THE TRIGGER only if it's in the schema's
  // `required` array AND has no `default` — a default-bearing field is
  // satisfiable without input (Zod `.default()` fields land in `required` but
  // need not be filled), so it's treated as optional here.
  const isReqField = useCallback(
    (name, def) => requiredSet.has(name) && def?.default === undefined,
    // requiredSet is derived from schema each render; depend on schema.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema],
  );

  // Live property-docs map. Kept in a ref so the hover provider (registered
  // ONCE on mount) always reads the current schema/hideInternal-derived docs
  // without needing to re-register.
  const propertyDocsRef = useRef(buildPropertyDocs(schema, isReqField, hideInternal, hideAdvanced));
  useEffect(() => {
    propertyDocsRef.current = buildPropertyDocs(schema, isReqField, hideInternal, hideAdvanced);
  }, [schema, isReqField, hideInternal, hideAdvanced]);

  // ── Required-field ghost labels ────────────────────────────────────────────
  // Names of the top-level fields that are required FOR THE TRIGGER (in
  // `required`, no default, not trigger-hidden). Kept in a ref so applyReqGhosts
  // — invoked from the editor change handler — reads the current set without
  // re-binding.
  const reqNamesRef = useRef(new Set());

  // Render a ghost "// required" annotation at the end of each required field's
  // line via a Monaco INJECTED-TEXT decoration. Injected text is NOT part of the
  // document — it never reaches JSON.parse and draws no error squiggle; it's a
  // pure inlay hint. Re-derived from the live text on every change so it tracks
  // keys being added / removed / reordered. Best-effort: cleared if no editor.
  const applyReqGhosts = useCallback((value) => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;
    const model = editor.getModel();
    if (!model) return;

    const reqNames = reqNamesRef.current;
    const next = [];
    if (reqNames && reqNames.size > 0 && value) {
      const lines = value.split('\n');
      // Top-level key at the start of a line (after indent):  "name":
      const keyRe = /^(\s*)"([^"\\]+)"\s*:/;
      for (let i = 0; i < lines.length; i++) {
        const m = keyRe.exec(lines[i]);
        if (!m || !reqNames.has(m[2])) continue;
        // Zero-width range at the logical end of the line; the injected `after`
        // text then renders just past the value/comma (1-based columns).
        const endCol = lines[i].length + 1;
        next.push({
          range: new monaco.Range(i + 1, endCol, i + 1, endCol),
          options: {
            after: {
              content: '  // required',
              inlineClassName: 'json-input-req-ghost',
            },
          },
        });
      }
    }
    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, next);
  }, []);

  // Recompute the required-name set (and refresh the ghosts) whenever the schema
  // / hideInternal inputs change — e.g. the workflow row loads after the modal
  // already opened.
  useEffect(() => {
    const names = new Set();
    if (schema?.properties) {
      for (const [name, def] of Object.entries(schema.properties)) {
        if (hideInternal && isTriggerHidden(def)) continue;
        // Required fields are always seeded (even if advanced), so they keep a
        // ghost. Non-required advanced fields collapsed out of the seed don't.
        if (isReqField(name, def)) names.add(name);
      }
    }
    reqNamesRef.current = names;
    applyReqGhosts(editorRef.current?.getModel()?.getValue() ?? '');
  }, [schema, isReqField, hideInternal, hideAdvanced, applyReqGhosts]);

  const validateAndReport = useCallback((nextText) => {
    if (!nextText || !nextText.trim()) {
      setParseError(null);
      onChange?.({}, { ok: true, error: null });
      return;
    }
    try {
      const parsed = JSON.parse(nextText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        const err = 'Input must be a JSON object (e.g. { "key": "value" }).';
        setParseError(err);
        onChange?.(null, { ok: false, error: err });
        return;
      }
      setParseError(null);
      onChange?.(parsed, { ok: true, error: null });
    } catch (e) {
      const err = e.message.replace(/^JSON\.parse: /, '');
      setParseError(err);
      onChange?.(null, { ok: false, error: err });
    }
  }, [onChange]);

  // Re-seed when schema or initialValue arrives async (e.g. modal opened
  // before the workflow row finished loading). We only overwrite if the
  // user hasn't typed yet — heuristic: text matches the previous example.
  const seededRef = useRef(text);
  useEffect(() => {
    const fresh = seedFromProps();
    if (text === seededRef.current) {
      setText(fresh);
      seededRef.current = fresh;
      validateAndReport(fresh);
      requestAnimationFrame(() => applyReqGhosts(fresh));
    }
    // Re-seed (only when untouched) whenever the schema, the saved default, OR
    // the advanced-field toggle changes — so switching tabs or revealing
    // advanced fields refreshes the example payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, initialValue, hideAdvanced]);

  // Initial report so parent's `ok` reflects the seeded state.
  useEffect(() => { validateAndReport(text); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dispose the hover provider on unmount so multiple JsonInput instances (and
  // remounts) don't leak/stack providers.
  useEffect(() => {
    return () => {
      hoverDisposableRef.current?.dispose?.();
      hoverDisposableRef.current = null;
    };
  }, []);

  const handleEditorChange = useCallback((value) => {
    const next = value ?? '';
    setText(next);
    validateAndReport(next);
    // Keep the "// required" ghosts in sync as keys are added / removed / moved.
    applyReqGhosts(next);
  }, [validateAndReport, applyReqGhosts]);

  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Register the JSON hover provider, SCOPED to this editor's model so other
    // Monaco/JsonInput instances on the page don't cross-pollinate. Monaco's
    // provider API is global-per-language, so we guard on model.uri and read
    // the live property-docs map from the ref.
    const myUri = editor.getModel()?.uri?.toString();
    hoverDisposableRef.current = monaco.languages.registerHoverProvider('json', {
      provideHover: (model, position) => {
        if (model.uri.toString() !== myUri) return null;
        const word = model.getWordAtPosition(position);
        if (!word) return null;
        const name = word.word;
        const doc = propertyDocsRef.current.get(name);
        if (!doc) return null;
        const reqLabel = doc.required ? 'required' : 'optional';
        return {
          range: new monaco.Range(
            position.lineNumber, word.startColumn,
            position.lineNumber, word.endColumn,
          ),
          contents: [
            { value: `**${name}** (${doc.type}, ${reqLabel})` },
            { value: doc.description },
          ],
        };
      },
    });

    // First ghost pass against the seeded value.
    applyReqGhosts(editor.getModel()?.getValue() ?? '');
  }, [applyReqGhosts]);

  return (
    <div className="json-input">
      <div className="json-input-toolbar">
        <span className="json-input-toolbar-label">{label}</span>
        {/* Discoverability cue, inline on the right: documented keys are
            hoverable for docs. Shown only when there's something to hover. */}
        {propertyDocsRef.current.size > 0 && (
          <span className="json-input-hover-hint">Hover a field for its description</span>
        )}
      </div>

      <div className={`json-input-editor ${parseError ? 'has-error' : ''}`}>
        <Editor
          height="368px"
          defaultLanguage="json"
          language="json"
          theme="vs-dark"
          loading={<div className="json-input-editor-loading">Loading editor…</div>}
          value={text}
          onChange={handleEditorChange}
          onMount={handleEditorMount}
          options={{
            readOnly: disabled,
            minimap: { enabled: false },
            fontSize: 12.5,
            lineNumbers: 'on',
            // Narrow the left gutter: fit 2-digit line numbers and drop the
            // wide empty fold/glyph columns Monaco reserves by default.
            lineNumbersMinChars: 2,
            folding: false,
            glyphMargin: false,
            lineDecorationsWidth: 6,
            scrollBeyondLastLine: false,
            // Render hover/suggest overflow widgets attached to the body, so a
            // tooltip on a TOP-row key isn't clipped by the editor wrapper's
            // overflow:hidden (it used to get cut off at the top edge).
            fixedOverflowWidgets: true,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            renderLineHighlight: 'none',
            scrollbar: { vertical: 'auto', horizontal: 'auto' },
            // Hovers carry our field docs — keep them enabled even read-only.
            hover: { enabled: true },
          }}
        />
      </div>

      {parseError && (
        <p className="json-input-error">⚠ {parseError}</p>
      )}
    </div>
  );
}
